import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { ChevronDown, Leaf, ShieldCheck, Package, Truck, ArrowRight, Star } from 'lucide-react';
import { TESTIDS } from '@/constants/testIds';

const HERO_IMG = '/images/hero-banner.jpg';
const SPICE_MACRO = 'https://images.pexels.com/photos/3040873/pexels-photo-3040873.jpeg';
const SPICE_DARK = 'https://images.pexels.com/photos/37568646/pexels-photo-37568646.jpeg';
const CHEF_IMG = 'https://images.pexels.com/photos/34021716/pexels-photo-34021716.jpeg';

const IMG_RED_CHILLI = '/images/red-chilli-powder.jpg';
const IMG_TURMERIC = '/images/turmeric-powder.jpg';
const IMG_CORIANDER = '/images/coriander-powder.jpg';
const IMG_GARAM = '/images/garam-masala.jpg';
const IMG_KANDA = '/images/kanda-masala.jpg';

const products = [
  { nameEn: 'Red Chilli Powder', nameHi: 'लाल मिर्च पावडर', desc: 'Bold & vibrant heat for every dish', category: 'powder', image: IMG_RED_CHILLI },
  { nameEn: 'Turmeric Powder', nameHi: 'हळद पावडर', desc: 'Golden pure turmeric from Sangli & Hingoli', category: 'powder', image: IMG_TURMERIC },
  { nameEn: 'Coriander Powder', nameHi: 'धने पावडर', desc: 'Fresh & earthy aroma for curries', category: 'powder', image: IMG_CORIANDER },
  { nameEn: 'Garam Masala', nameHi: 'गरम मसाला', desc: 'A warming blend of finest whole spices', category: 'blended', image: IMG_GARAM },
  { nameEn: 'Kanda Lasun Masala', nameHi: 'कांदा लसूण मसाला', desc: 'Traditional onion-garlic spice blend', category: 'blended', image: IMG_KANDA },
];

const trustItems = [
  'FSSAI Certified', '100% Natural', 'No Artificial Colours', '30+ Masala Varieties',
  'B2B Supply Ready', 'Hygienically Packed', 'Rooted in India',
];

function AnimatedCounter({ target, suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  useEffect(() => {
    if (!isInView) return;
    const duration = 2000;
    const steps = 80;
    const interval = duration / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (step >= steps) { setCount(target); clearInterval(timer); }
    }, interval);
    return () => clearInterval(timer);
  }, [isInView, target]);
  const formatted = count.toLocaleString('en-IN');
  return <span ref={ref}>{formatted}{suffix}</span>;
}

/* Mandala SVG Watermark */
function MandalaWatermark({ className = '' }) {
  return (
    <svg viewBox="0 0 200 200" className={`absolute pointer-events-none ${className}`} fill="none" stroke="currentColor" strokeWidth="0.5">
      <circle cx="100" cy="100" r="95" /><circle cx="100" cy="100" r="75" /><circle cx="100" cy="100" r="55" />
      {Array.from({ length: 16 }).map((_, i) => (
        <line key={i} x1="100" y1="5" x2="100" y2="30" transform={`rotate(${i * 22.5} 100 100)`} />
      ))}
      {Array.from({ length: 8 }).map((_, i) => (
        <ellipse key={`e${i}`} cx="100" cy="40" rx="12" ry="25" transform={`rotate(${i * 45} 100 100)`} />
      ))}
    </svg>
  );
}

export default function HomePage() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.3]);

  return (
    <div data-testid={TESTIDS.HOME_PAGE}>
      {/* ===== HERO SECTION ===== */}
      <section ref={heroRef} data-testid={TESTIDS.HERO_SECTION} className="relative h-screen overflow-hidden">
        <motion.div style={{ y: heroY }} className="absolute inset-0 -top-[10%] h-[120%]">
          <img src={HERO_IMG} alt="Anuruchi Masale Products" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/80 via-[#1A1A1A]/50 to-transparent" />
        </motion.div>

        {/* Spice Particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className={`spice-particle particle-${i + 1}`} />
          ))}
        </div>

        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 h-full flex items-center px-6 md:px-12">
          <div className="max-w-7xl mx-auto w-full">
            <div className="max-w-xl">
              <motion.p
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                data-testid={TESTIDS.HERO_TAGLINE}
                className="font-hindi text-[#C9922C] text-xl md:text-2xl mb-4"
              >
                अब प्यार के साथ स्वाद भी!!
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
                className="font-playfair text-white text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight mb-6"
              >
                Pure Spices.<br />Perfect Recipes.
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
                className="font-lato text-[#F9F3E8]/80 text-base md:text-lg max-w-lg mb-10"
              >
                Premium B2B spice supply for Hotels, Restaurants, Cafes & Cloud Kitchens across India
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link to="/products" data-testid={TESTIDS.HERO_EXPLORE_BTN} className="btn-gold">
                  Explore Products
                </Link>
                <Link to="/contact" data-testid={TESTIDS.HERO_QUOTE_BTN} className="btn-outline-gold">
                  Get a Quote
                </Link>
              </motion.div>
            </div>
          </div>
        </motion.div>

        <div className="absolute bottom-8 left-1/2 scroll-arrow z-10">
          <ChevronDown className="text-[#F9F3E8]/70" size={28} />
        </div>
      </section>

      {/* ===== TRUST BAR ===== */}
      <section data-testid={TESTIDS.TRUST_BAR} className="bg-[#F9F3E8] border-y border-[#C9922C]/20 py-4">
        <Marquee speed={40} gradient={false} pauseOnHover>
          {trustItems.concat(trustItems).map((item, i) => (
            <span key={i} className="trust-item">
              <Star size={10} className="text-[#C9922C] fill-[#C9922C]" />
              {item}
            </span>
          ))}
        </Marquee>
      </section>

      {/* ===== ABOUT SNAPSHOT ===== */}
      <section data-testid={TESTIDS.ABOUT_SECTION} className="bg-[#F9F3E8] py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-100px' }} transition={{ duration: 0.7 }}
            >
              <p className="font-cinzel text-xs tracking-[0.25em] uppercase text-[#C9922C] mb-4">Our Story</p>
              <h2 className="font-playfair text-[#1B3A6B] text-3xl md:text-5xl leading-tight mb-6">
                Rooted in India.<br />Ground with Love.
              </h2>
              <p className="font-lato text-[#5C3D11] text-base md:text-lg leading-relaxed mb-6">
                Anuruchi Masale was born from a passion for authentic Indian spices. Founded by Rushikesh, who spent years working with exporters and domestic wholesale clients, the brand brings heartland flavors directly to professional kitchens across India. Every batch is carefully cleaned, slow-ground, and packed to lock in real Indian taste.
              </p>
              <Link
                to="/about"
                data-testid={TESTIDS.ABOUT_READ_MORE}
                className="inline-flex items-center gap-2 font-cinzel text-xs tracking-[0.15em] uppercase text-[#C9922C] hover:text-[#E8A020] transition-colors group"
              >
                Read More <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-100px' }} transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              <div className="relative">
                <img src={SPICE_MACRO} alt="Premium spices" className="w-full h-72 md:h-96 object-cover rounded-lg shadow-xl" loading="lazy" />
                <div className="absolute -bottom-6 -left-6 w-40 h-40 md:w-52 md:h-52 rounded-lg overflow-hidden shadow-2xl border-4 border-[#F9F3E8] hidden md:block">
                  <img src={HERO_IMG} alt="Anuruchi products" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== ANIMATED COUNTER ===== */}
      <section data-testid="counter-section" className="bg-[#1B3A6B] py-20 md:py-28 relative overflow-hidden">
        <MandalaWatermark className="w-80 h-80 text-[#C9922C] opacity-[0.04] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.6 }}
          >
            <p className="font-playfair text-[#C9922C] text-5xl sm:text-6xl md:text-8xl font-bold mb-2">
              <AnimatedCounter target={45000} suffix="+" />
            </p>
            <div className="w-16 h-0.5 bg-[#C9922C] mx-auto mb-6" />
            <p className="font-lato text-white text-lg md:text-xl leading-relaxed">
              Served With Our Spices Everyday
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== PRODUCTS SHOWCASE ===== */}
      <section data-testid={TESTIDS.PRODUCTS_SECTION} className="bg-white py-20 md:py-32 relative overflow-hidden">
        <MandalaWatermark className="w-64 h-64 text-[#1B3A6B] opacity-[0.04] -top-20 -right-20" />
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="font-cinzel text-xs tracking-[0.25em] uppercase text-[#C9922C] mb-3">Premium Range</p>
            <h2 className="font-playfair text-[#1B3A6B] text-3xl md:text-5xl">Our Premium Spice Range</h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, i) => (
              <motion.div
                key={i}
                data-testid={`${TESTIDS.PRODUCT_CARD}-${i}`}
                initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="gold-corners bg-[#F9F3E8] border border-[#C9922C]/15 hover:border-[#C9922C]/60 hover:shadow-xl transition-all duration-500 group p-1"
              >
                <div className="overflow-hidden rounded-sm mx-2 mt-2">
                  <img src={product.image} alt={product.nameEn} className="w-full h-auto object-cover rounded-sm group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-4">
                  <p className="font-hindi text-[#C9922C] text-sm mb-0.5">{product.nameHi}</p>
                  <h4 className="font-playfair text-[#1B3A6B] text-lg font-semibold mb-1">{product.nameEn}</h4>
                  <p className="font-lato text-[#5C3D11] text-xs leading-relaxed mb-3">{product.desc}</p>
                  <span className="inline-block font-cinzel text-[9px] tracking-[0.15em] uppercase text-[#C9922C] border border-[#C9922C]/30 px-3 py-1">
                    Premium Quality
                  </span>
                </div>
              </motion.div>
            ))}

            {/* +30 More Card */}
            <motion.div
              initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.4 }}
              className="flex items-center justify-center bg-[#1B3A6B] p-8 text-center min-h-[350px]"
            >
              <div>
                <p className="font-playfair text-[#C9922C] text-5xl font-bold mb-3">30+</p>
                <p className="font-cinzel text-[#F9F3E8] text-xs tracking-wider uppercase mb-2">Products Available</p>
                <p className="font-lato text-[#F9F3E8]/50 text-sm mb-6">Including whole spices, powders & custom blends</p>
                <Link to="/contact" className="btn-outline-gold text-xs py-2.5 px-5">
                  Ask for Full Catalog
                </Link>
              </div>
            </motion.div>
          </div>

        </div>
      </section>

      {/* ===== WHY CHOOSE US ===== */}
      <section data-testid={TESTIDS.WHY_CHOOSE_SECTION} className="bg-[#1B3A6B] py-20 md:py-32 relative overflow-hidden">
        <MandalaWatermark className="w-96 h-96 text-[#C9922C] opacity-[0.05] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="font-cinzel text-xs tracking-[0.25em] uppercase text-[#C9922C] mb-3">Our Promise</p>
            <h2 className="font-playfair text-[#F9F3E8] text-3xl md:text-5xl">Why Choose Anuruchi?</h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Leaf, title: '100% Natural', desc: 'No additives, preservatives, or artificial colours in any of our products' },
              { icon: ShieldCheck, title: 'FSSAI Certified', desc: 'License No. 21524031001278 — fully compliant with food safety standards' },
              { icon: Package, title: 'Hygienically Packed', desc: 'Sealed resealable pouches, freshness and aroma guaranteed' },
              { icon: Truck, title: 'B2B Bulk Ready', desc: 'Consistent, reliable supply for large-scale professional kitchens' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.12 }}
                className="text-center border-b border-[#C9922C]/20 pb-8"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-[#C9922C]/40 mb-5">
                  <item.icon size={28} className="text-[#C9922C]" />
                </div>
                <h3 className="font-playfair text-[#F9F3E8] text-xl mb-3">{item.title}</h3>
                <p className="font-lato text-[#F9F3E8]/65 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CLIENT SEGMENTS ===== */}
      <section
        data-testid={TESTIDS.CLIENT_SECTION}
        className="relative py-20 md:py-32 overflow-hidden"
      >
        <div
          className="absolute inset-0 parallax-bg"
          style={{ backgroundImage: `url(${SPICE_DARK})` }}
        />
        <div className="absolute inset-0 bg-[#1A1A1A]/75" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="font-cinzel text-xs tracking-[0.25em] uppercase text-[#C9922C] mb-3">B2B Supply</p>
            <h2 className="font-playfair text-white text-3xl md:text-5xl">Who We Supply To</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: 'Restaurants & Cafes', desc: 'Consistent bulk spice supply for daily kitchen operations across dine-in establishments' },
              { title: 'Cloud Kitchens', desc: 'Portion-perfect blends for high-volume delivery kitchens with rapid turnaround' },
              { title: 'Catering Companies', desc: 'Custom masala solutions for events, weddings, and institutional catering at scale' },
            ].map((seg, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.15 }}
                className="bg-[#F9F3E8] p-8 border-t-4 border-[#C9922C] shadow-xl"
              >
                <h3 className="font-playfair text-[#1B3A6B] text-xl mb-3">{seg.title}</h3>
                <p className="font-lato text-[#5C3D11] text-sm leading-relaxed">{seg.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== STATS BAR ===== */}
      <section data-testid={TESTIDS.STATS_SECTION} className="bg-[#F9F3E8] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: 30, suffix: '+', label: 'Masala Varieties' },
              { value: 100, suffix: '%', label: 'Natural Ingredients' },
              { value: 0, suffix: '', label: 'Pan-India', isText: true },
              { value: 0, suffix: '', label: 'FSSAI Certified', isText: true },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <p className="font-playfair text-[#C9922C] text-4xl md:text-5xl font-bold mb-2">
                  {stat.isText ? stat.label.split(' ')[0] : <AnimatedCounter target={stat.value} suffix={stat.suffix} />}
                </p>
                <p className="font-cinzel text-[#5C3D11] text-xs tracking-[0.15em] uppercase">
                  {stat.isText ? stat.label.split(' ').slice(1).join(' ') || 'Certified' : stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PACKAGING SHOWCASE ===== */}
      <section data-testid={TESTIDS.PACKAGING_SECTION} className="bg-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7 }}
            >
              <img src={HERO_IMG} alt="Anuruchi Masale Packaging" className="w-full h-72 md:h-[450px] object-cover rounded-lg shadow-2xl" loading="lazy" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 }}
            >
              <p className="font-cinzel text-xs tracking-[0.25em] uppercase text-[#C9922C] mb-3">Packaging</p>
              <h2 className="font-playfair text-[#1B3A6B] text-3xl md:text-4xl mb-8">Built for Professional Kitchens</h2>
              <ul className="space-y-4">
                {[
                  'Resealable zipper pouches for extended freshness',
                  'Moisture-proof barrier to preserve aroma',
                  'Clearly labeled with ingredients, batch no., MRP, EXP date',
                  'Available in multiple SKU sizes for B2B requirements',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-1.5 w-2 h-2 rounded-full bg-[#C9922C] flex-shrink-0" />
                    <span className="font-lato text-[#5C3D11] text-base leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" data-testid={TESTIDS.REQUEST_SAMPLES_BTN} className="btn-gold mt-8 inline-flex">
                Request Samples
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== CONTACT CTA BANNER ===== */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 parallax-bg" style={{ backgroundImage: `url(${CHEF_IMG})` }} />
        <div className="absolute inset-0 bg-[#1A1A1A]/80" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.6 }}
          >
            <h2 className="font-playfair text-white text-3xl md:text-5xl mb-4">Ready to Elevate Your Kitchen?</h2>
            <p className="font-lato text-[#F9F3E8]/75 text-base md:text-lg mb-10">
              Get in touch for bulk pricing, product samples, and custom blends.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+917774926163" data-testid={TESTIDS.CONTACT_CTA_CALL} className="btn-gold">
                Call Now: +91-7774926163
              </a>
              <Link to="/contact" data-testid={TESTIDS.CONTACT_CTA_EMAIL} className="btn-outline-gold">
                Email Us
              </Link>
            </div>
            <p className="font-lato text-[#F9F3E8]/40 text-xs mt-8">Maprix Industries Pvt. Ltd.</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
